import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { CareCreditEnrollmentMultiStep } from '@frontend/payments-care-credit';
import { CareCreditPatientData } from '@frontend/payments-care-credit';
import { currency, MultiStep, Text } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CareCreditEnrollStepBodyProps } from './care-credit-enroll.types';

const styles = {
  viewContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
    width: 100%;
  `,
};

export const CareCreditEnrollBody = ({
  view,
  onGoBack,
  onCancel,
  onEnrollmentAccepted,
  onEnrollmentDeclined,
  onGoToMessageStep,
}: CareCreditEnrollStepBodyProps) => {
  const onComplete = useCallback(() => {
    onGoBack();
  }, [onGoBack]);

  const [stepConfig, setStepConfig] = useState({
    showHeader: true,
  });

  const { person, invoice, careCreditData, activeLocationId, setFlowAndStep } = useCollectPaymentMultiContext();

  const onSetParentStepData = useCallback(({ showHeader }: { showHeader: boolean }) => {
    setStepConfig({ showHeader });
  }, []);

  const onClickGoToPayment = useCallback(() => {
    setFlowAndStep('select', PAYMENT_MULTI_STEPS.select);
  }, [setFlowAndStep]);

  if (!!view) {
    return (
      <CareCreditEnrollCompletionView
        view={view}
        onCancel={onCancel}
        onClickGoToPayment={onClickGoToPayment}
        person={person}
        careCreditData={careCreditData}
      />
    );
  }

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditEnroll}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditEnroll]}
      hasInternalFooter
      showBlueHeader={true}
      hasChildStepper
      components={{
        Footer: () => null,
        Header: stepConfig.showHeader ? undefined : () => null,
      }}
    >
      <CareCreditEnrollmentMultiStep
        multiStepProps={{
          onComplete,
          onCancel,
        }}
        invoice={invoice}
        person={person}
        locationId={activeLocationId}
        careCreditData={careCreditData}
        onCancel={onCancel}
        onGoBack={onGoBack}
        onGoToMessageStep={onGoToMessageStep}
        onEnrollmentAccepted={onEnrollmentAccepted}
        onEnrollmentDeclined={onEnrollmentDeclined}
        onSetParentStepData={onSetParentStepData}
      />
    </StyledStep>
  );
};

type CareCreditEnrollCompletionViewProps = {
  view: 'success' | 'declined';
  onCancel?: () => void;
  onClickGoToPayment: () => void;
  person: Person | undefined;
  careCreditData: CareCreditPatientData | undefined;
};
const CareCreditEnrollCompletionView = ({
  view,
  person,
  careCreditData,
  onCancel,
  onClickGoToPayment,
}: CareCreditEnrollCompletionViewProps) => {
  const { t } = useTranslation('payments');

  if (view === 'success') {
    return (
      <StyledStep
        id={PAYMENT_MULTI_STEPS.careCreditEnroll}
        label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditEnroll]}
        isValid={true}
        isComplete={true}
        components={{
          Header: (props) => <MultiStep.Step.Header {...props} showCancelAction title={t('Applicant Approved')} />,
        }}
        footer={{
          primary: {
            label: t('Go to Payment'),
          },
          onClickPrimary: onClickGoToPayment,
          secondary: {
            label: t('Close'),
          },
          onClickSecondary: () => {
            onCancel?.();
          },
          showBackButton: false,
        }}
      >
        <div css={styles.viewContainer}>
          <Text>
            {careCreditData?.amount !== undefined && (
              <Trans
                t={t}
                components={{ b: <b /> }}
                i18nKey={`{{fullName}} is approved for a CareCredit credit card with a <b>{{amount}} credit limit.</b>`}
                values={{
                  fullName: getFullName({ firstName: person?.firstName, lastName: person?.lastName }),
                  amount: currency(careCreditData.amount || 0, {
                    decimals: 0,
                  }),
                }}
              />
            )}
            {!careCreditData?.amount && (
              <Trans
                t={t}
                components={{ b: <b /> }}
                i18nKey={`{{fullName}} is approved for a CareCredit credit card.`}
                values={{
                  fullName: getFullName({ firstName: person?.firstName, lastName: person?.lastName }),
                }}
              />
            )}
          </Text>
        </div>
      </StyledStep>
    );
  }

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditEnroll}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditEnroll]}
      components={{
        Header: (props) => <MultiStep.Step.Header {...props} showCancelAction title={t('Application Declined')} />,
      }}
      footer={{
        primary: null,
        secondary: {
          label: t('Close'),
        },
        showBackButton: false,
        onClickSecondary: () => {
          onCancel?.();
        },
      }}
    >
      <div css={styles.viewContainer}>
        <Text>
          {t(
            'CareCredit was unable to approve this application at this time.  A written explanation will be sent in the mail within 7-10 days regarding the reasons for the decision and the source of the information utilized.  It will include a number to call for questions.'
          )}
        </Text>
      </div>
    </StyledStep>
  );
};
